import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { TextField } from '../index';

import type { DraftSet, WeightUnit } from './state/loggerDraft';
import type { useTheme } from './theme';
import { decomposeHold, holdCellNum, recomposeHold } from './holdTime';
import { steppedWeight } from './weightStepper';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';
import { SetRowStepperBar } from './SetRowStepperBar';
import { STEPS_KG, STEPS_LB } from './weightStepConstants';

// Timed (isometric) inputs for the timeline FocusPanel — plank, wall-sit,
// bird-dog hold, the physiotherapy isometrics. Same layout as the strength
// fields (FocusPanelInputs): labelled bare-number cells, no inline unit
// suffix crowding the digit (Antenore 2026-06-09: "usa lo stesso layout dei
// pesi"). Columns: h · min · s · extra-load. Duration is one DraftSet.
// duration (seconds); h/min/s derive + recompose via holdTime. The
// start-timer affordance is no longer a row here: it moved into the effort
// meter's slot below (MX-5 slice 2), so this block is only the fields. The
// added-load cell stays editable always (the load is a setup property, entered
// before or after the hold).
export function FocusPanelTimed({
  set,
  defaultUnit,
  onChange,
  theme,
}: {
  set: DraftSet;
  defaultUnit: WeightUnit;
  onChange: (patch: Partial<Pick<DraftSet, 'duration' | 'weight'>>) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // Added-load gets the SAME +X kg stepper as the strength weight field,
  // revealed on focus (Antenore 2026-06-09: "l'extra non ha il selettore
  // di peso come gli esercizi standard").
  const [extraFocused, setExtraFocused] = useState(false);
  const [stepSign, setStepSign] = useState<1 | -1>(1);
  const applyDelta = (delta: number) =>
    onChange({ weight: steppedWeight(set.weight, delta) });

  const { h, m, s } = decomposeHold(set.duration);
  const commit = (hh: number, mm: number, ss: number) =>
    onChange({ duration: recomposeHold(hh, mm, ss) });

  // Tap a populated cell → select its digits so you type over them (the
  // focus reveal of the hold-start button re-renders the row and clears RN's
  // native selectTextOnFocus on Android — see useSelectAllOnFocus).
  const hSel = useSelectAllOnFocus(h > 0 ? String(h) : '');
  const mSel = useSelectAllOnFocus(m > 0 ? String(m) : '');
  const sSel = useSelectAllOnFocus(s > 0 ? String(s) : '');
  const extraSel = useSelectAllOnFocus(set.weight);

  const timeField = (
    label: string,
    value: number,
    onNum: (n: number) => void,
    a11y: string,
    sel: ReturnType<typeof useSelectAllOnFocus>,
  ) => (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextField
        theme={theme}
        numeric
        ref={sel.ref}
        keyboardType="number-pad"
        inputMode="numeric"
        accessibilityLabel={a11y}
        placeholder="0"
        value={value > 0 ? String(value) : ''}
        onChangeText={(txt) => onNum(holdCellNum(txt))}
        onFocus={sel.onFocus}
        selectTextOnFocus
        maxLength={2}
      />
    </View>
  );

  return (
    <View style={styles.block}>
      <View style={styles.row}>
        {timeField(
          t('mobile.logger.holdHeaderHours', { defaultValue: 'h' }),
          h,
          (n) => commit(n, m, s),
          t('mobile.logger.holdHours', { defaultValue: 'Hours' }),
          hSel,
        )}
        {timeField(
          t('mobile.logger.holdHeaderMinutes', { defaultValue: 'min' }),
          m,
          (n) => commit(h, n, s),
          t('mobile.logger.holdMinutes', { defaultValue: 'Minutes' }),
          mSel,
        )}
        {timeField(
          t('mobile.logger.holdHeaderSeconds', { defaultValue: 's' }),
          s,
          (n) => commit(h, m, n),
          t('mobile.logger.holdSeconds', { defaultValue: 'Seconds' }),
          sSel,
        )}
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>
            {`${t('mobile.logger.holdExtraWeight', { defaultValue: 'Extra' })} (${defaultUnit})`}
          </Text>
          <TextField
            theme={theme}
            numeric
            ref={extraSel.ref}
            inputMode="decimal"
            placeholder="—"
            value={set.weight}
            onChangeText={(weight) => onChange({ weight })}
            onFocus={() => {
              setExtraFocused(true);
              extraSel.onFocus();
            }}
            onBlur={() => setExtraFocused(false)}
            selectTextOnFocus
            maxLength={6}
          />
        </View>
      </View>
      {extraFocused ? (
        <SetRowStepperBar
          unit={defaultUnit}
          onToggleUnit={() => {}}
          stepSign={stepSign}
          setStepSign={setStepSign}
          stepMagnitudes={defaultUnit === 'lb' ? STEPS_LB : STEPS_KG}
          applyDelta={applyDelta}
          theme={theme}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { flex: 1, gap: s.s2 },
    row: { flexDirection: 'row', alignItems: 'flex-end', gap: s.s2 },
    // Mirrors FocusPanelInputs' field/fieldLabel/input so a timed set reads
    // identically to a strength set in the timeline panel.
    field: { flex: 1, gap: 4 },
    fieldLabel: { color: p.ink3, fontSize: ty.sizes.xs, fontWeight: '600' },
  });
}
